import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faTrash,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../../common/RefreshButton";
import LoadingSpinner from "../../../common/LoadingSpinner";
import { formatDateOnly } from "../../../utils/DateTimeUtil";
import { useTranslation } from "../../../../../i18n/LanguageContext";

const AnalysisHistory = ({
  analysisHistory,
  historyLoading,
  selectedHistoryItem,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  onViewHistoryItem,
  onDeleteHistoryItem,
  onRefreshHistory,
  isMobile,
  historyPanelVisible,
  setHistoryPanelVisible,
  embedded = false,
}) => {
  // Helper functions
  const isSystemError = (item) => {
    if (!item || !item.explanation) return false;
    return (
      item.explanation.includes("SYSTEM_ERROR") ||
      item.explanation.includes(
        "Analysis was unavailable due to processing error",
      ) ||
      item.explanation.includes("Unable to analyze") ||
      (item.confidence === 0 && item.explanation.includes("technical error"))
    );
  };

  const hasInsufficientData = (item) => {
    if (!item) return false;
    return (
      item.dataSufficiency === "INSUFFICIENT" ||
      item.explanation?.includes("Insufficient Data") ||
      item.explanation?.includes("insufficient data") ||
      item.explanation?.includes("Not enough reliable information") ||
      (item.confidence && item.confidence < 0.4)
    );
  };

  const getRiskBadgeText = (item) => {
    if (isSystemError(item)) {
      return "Analysis Unavailable";
    }
    if (item.analysisStatus) {
      return item.analysisStatus;
    }
    if (hasInsufficientData(item)) {
      return "Insufficient Data";
    }
    return item.isScam ? "Potential Scam Detected" : "Appears Legitimate";
  };

  const getStatusClass = (item) => {
    if (isSystemError(item)) {
      return "system-error";
    }
    if (item.analysisStatus) {
      switch (item.analysisStatus) {
        case "Very likely a scam":
          return "very-high-risk";
        case "Potentially a scam":
          return "high-risk";
        case "Potentially a scam (Insufficient data)":
          return "medium-risk-insufficient";
        case "Unlikely a scam (Insufficient data)":
          return "low-risk-insufficient";
        case "Unlikely a scam":
          return "low-risk";
        case "Legitimate":
          return "low-risk";
        default:
          return "unknown-risk";
      }
    }
    if (hasInsufficientData(item)) {
      return "insufficient-data";
    }
    return item.isScam ? "high-risk" : "low-risk";
  };

  const getStatusIcon = (item) => {
    if (isSystemError(item)) {
      return faExclamationTriangle;
    }
    if (item.analysisStatus) {
      switch (item.analysisStatus) {
        case "Very likely a scam":
          return faTimesCircle;
        case "Potentially a scam":
          return faTimesCircle;
        case "Potentially a scam (Insufficient data)":
          return faExclamationTriangle;
        case "Unlikely a scam (Insufficient data)":
          return faExclamationTriangle;
        case "Unlikely a scam":
          return faExclamationTriangle;
        case "Legitimate":
          return faCheckCircle;
        default:
          return faExclamationTriangle;
      }
    }
    if (hasInsufficientData(item)) {
      return faExclamationTriangle;
    }
    return item.isScam ? faTimesCircle : faCheckCircle;
  };

  // Sort history by date descending (newest first)
  const sortedHistory = useMemo(() => {
    return [...analysisHistory].sort((a, b) => {
      const aDate = new Date(a.date || 0);
      const bDate = new Date(b.date || 0);
      return bDate - aDate;
    });
  }, [analysisHistory]);

  const { t } = useTranslation();

  // Pagination calculations
  const totalPages = Math.ceil(sortedHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = sortedHistory.slice(startIndex, endIndex);

  return (
    <div
      className={
        embedded
          ? "left-panel-tab-content"
          : `center-panel${!isMobile && historyPanelVisible ? " drawer-open" : ""}`
      }
    >
      {/* Mobile History Toggle */}
      {isMobile && (
        <div
          className="mobile-history-toggle-js"
          style={{
            display: "block",
            padding: "1rem",
            background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            margin: "0 0 1rem 0",
            textAlign: "center",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <button
              onClick={() => setHistoryPanelVisible(!historyPanelVisible)}
              className={`btn btn-secondary history-toggle ${
                historyPanelVisible ? "active" : ""
              }`}
              style={{
                background: historyPanelVisible
                  ? "linear-gradient(135deg, #4c51bf 0%, #553c9a 100%)"
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "2px solid rgba(102, 126, 234, 0.3)",
                padding: "0.75rem 1.5rem",
                borderRadius: "8px",
                fontWeight: "500",
                fontSize: "0.9rem",
                width: "100%",
                minHeight: "44px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
              title={
                historyPanelVisible
                  ? t("social.history.hideTitle")
                  : t("social.history.showTitle")
              }
            >
              <FontAwesomeIcon icon={faHistory} />
              {historyPanelVisible
                ? t("social.history.hide")
                : t("social.history.show")}
            </button>
          </div>
        </div>
      )}

      {/* History Content - Always rendered on desktop (drawer handles visibility), conditional on mobile */}
      {(!isMobile || historyPanelVisible) && (
        <>
          {!embedded && (
            <div className="common-section-header">
              <h3>
                <FontAwesomeIcon icon={faHistory} />
                {t("social.history.heading")}
                <span className="case-count">
                  {t("social.history.caseCount").replace(
                    "{count}",
                    sortedHistory.length,
                  )}
                </span>
              </h3>
            </div>
          )}

          <div className="panel-content history-panel-content">
            {/* Controls */}
            <div className="history-controls">
              <div className="search-group">
                <RefreshButton
                  onClick={onRefreshHistory}
                  loading={historyLoading}
                />
              </div>
            </div>

            {/* History List */}
            <div className="history-list">
              {historyLoading ? (
                <LoadingSpinner
                  size="sm"
                  message={t("social.history.loadingCases")}
                  className="compact"
                />
              ) : sortedHistory.length === 0 ? (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faHistory} />
                  <p>{t("social.history.noResults")}</p>
                  <p className="help-text">{t("social.history.submitHint")}</p>
                </div>
              ) : (
                paginatedHistory.map((item) => (
                  <div
                    key={item.id}
                    className={`history-item ${
                      selectedHistoryItem?.id === item.id ? "active" : ""
                    }`}
                    onClick={() => {
                      onViewHistoryItem(item);
                    }}
                  >
                    {/* Top row: Risk badge and Delete button */}
                    <div className="analysis-item-header">
                      <div className={`risk-badge ${getStatusClass(item)}`}>
                        <FontAwesomeIcon icon={getStatusIcon(item)} />
                        {getRiskBadgeText(item)}
                      </div>

                      <button
                        className="delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteHistoryItem(item);
                        }}
                        title="Delete this analysis"
                      >
                        Delete
                      </button>
                    </div>

                    {/* Business name row */}
                    <div className="item-title">
                      <h4>
                        {item.businessName ||
                          item.websiteUrl ||
                          t("social.history.unknownBusiness")}
                      </h4>
                    </div>

                    {/* Tags and Date row */}
                    <div
                      className="item-details"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div className="tags-container">
                        {item.platform && (
                          <span className="platform-tag">{item.platform}</span>
                        )}
                        {item.merchandiseType && (
                          <span className="merchandise-tag">
                            {item.merchandiseType}
                          </span>
                        )}
                      </div>
                      <span
                        className="risk-date"
                        style={{ marginLeft: "auto" }}
                      >
                        {item.date
                          ? formatDateOnly(item.date, "short")
                          : t("social.history.unknown")}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                >
                  {t("social.history.previous")}
                </button>
                <span className="page-info">
                  {t("social.history.pageOf")
                    .replace("{page}", currentPage)
                    .replace("{total}", totalPages)}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                >
                  {t("social.history.next")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalysisHistory;
